import { AdSlot } from "@sphtech/web2-ads";
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

import { readLegacyGrapeshotTargeting } from "../trackers/grapeshotLegacy";
import { SDK_ADS_SITE_KEY } from "./config";
import type { PageAdsContext } from "./pageAds";
import {
  AdPlacementAssignment,
  createScanState,
  releaseAdPlacementClaim,
  scanAdPlacements,
  scanAiSummaryPlacements,
  scanGalleryLightboxPlacements,
} from "./placements";

const RESCAN_DEBOUNCE_MS = 200;
const MAS_COLLAPSE_MS = 200;
const LAYOUT_POSITION_SELECTOR = "[data-layout-position]";
const LIGHTBOX_MESSAGE_RE = /^lightbox-(?:open(?:-image)?|change)=(\d+)$/;
/** Rail IMUs whose sidebar container hides on an empty render (legacy parity). */
const RAIL_HIDE_ON_EMPTY = new Set(["imu2", "imu3", "imu4"]);
/**
 * Dispatched (bubbling) on an assignment's container after every GPT render,
 * so ancestor chrome outside this bridge's ownership — e.g. the
 * design-system `ReleasingLeaderboard` sticky wrapper — can react to fill
 * state without a cross-package import. Keep the name in sync with
 * `packages/design-system/src/components/Utility/Ads/ReleasingLeaderboard.tsx`.
 */
const SLOT_RENDER_ENDED_EVENT = "sdkAdSlotRenderEnded";

type WindowWithOptionalSphmgam = Omit<Window, "SPHMGAM"> & {
  SPHMGAM?: {
    cmd?: unknown[];
  };
};

function containsLayoutPosition(node: Node): node is Element {
  if (!(node instanceof Element)) return false;
  return (
    node.matches(LAYOUT_POSITION_SELECTOR) ||
    Boolean(node.querySelector(LAYOUT_POSITION_SELECTOR))
  );
}

function nodeListHasLayoutPosition(nodes: NodeList): boolean {
  // Iterate the live NodeList directly and short-circuit, instead of
  // materialising intermediate arrays per mutation record (avoids GC pressure
  // under frequent DOM churn like infinite scroll).
  for (const node of nodes) {
    if (containsLayoutPosition(node)) return true;
  }
  return false;
}

function addUniqueRoot(roots: Element[], root: Element) {
  if (roots.some((existing) => existing.contains(root))) return;
  for (let i = roots.length - 1; i >= 0; i -= 1) {
    const existing = roots[i];
    if (existing && root.contains(existing)) {
      roots.splice(i, 1);
    }
  }
  roots.push(root);
}

function collectAddedPlacementRoots(records: MutationRecord[]): Element[] {
  const roots: Element[] = [];
  records.forEach((record) => {
    for (const node of record.addedNodes) {
      if (containsLayoutPosition(node)) {
        addUniqueRoot(roots, node);
      }
    }
  });
  return roots;
}

function readAnalyticsStoryPath(container: Element): string | undefined {
  const article = container.closest("article");
  const dataTag = article?.querySelector("[data-analytics-article]");
  const path = dataTag?.getAttribute("data-analytics-article");

  return path || undefined;
}

function collectStoryPaths(root: Element, fallback?: string): string[] {
  const paths = new Set<string>();
  const closestArticlePath = readAnalyticsStoryPath(root);
  if (closestArticlePath) {
    paths.add(closestArticlePath);
  }
  if (root.matches("[data-analytics-article]")) {
    const path = root.getAttribute("data-analytics-article");
    if (path) paths.add(path);
  }
  root.querySelectorAll("[data-analytics-article]").forEach((tag) => {
    const path = tag.getAttribute("data-analytics-article");
    if (path) paths.add(path);
  });
  if (paths.size === 0 && fallback) {
    paths.add(fallback);
  }
  return [...paths];
}

function mergeConnectedAssignments(
  current: AdPlacementAssignment[],
  found: AdPlacementAssignment[],
  onDropped?: (assignment: AdPlacementAssignment) => void,
) {
  const connected: AdPlacementAssignment[] = [];
  current.forEach((assignment) => {
    if (assignment.container.isConnected) {
      connected.push(assignment);
      return;
    }
    onDropped?.(assignment);
  });
  if (found.length === 0 && connected.length === current.length) {
    return current;
  }
  return [...connected, ...found];
}

function collapseMasContainer(container: Element, onCollapsed: () => void) {
  if (!(container instanceof HTMLElement)) return;
  if (container.dataset.sdkMasClosing === "true") return;

  container.dataset.sdkMasClosing = "true";
  container.style.overflow = "hidden";
  container.style.padding = "0";

  // Animate the collapse on requestAnimationFrame so the browser paces frames
  // and coalesces layout work, instead of a 1ms setInterval tight loop.
  const startHeight = container.clientHeight;
  let start: number | undefined;
  const step = (now: number) => {
    start ??= now;
    const progress = Math.min((now - start) / MAS_COLLAPSE_MS, 1);
    container.style.height = `${startHeight * (1 - progress)}px`;

    if (progress >= 1) {
      container.style.display = "none";
      // The container is a React `createPortal` target, so its children are
      // owned by React. Never wipe them here (`innerHTML = ""`): that detaches
      // React-controlled nodes and makes the next portal unmount/reconcile
      // throw NotFoundError in commit (`removeChild` on a node that is no
      // longer a child). Drop the assignment instead and let React unmount the
      // AdSlot and remove its own nodes.
      onCollapsed();
      return;
    }
    window.requestAnimationFrame(step);
  };
  window.requestAnimationFrame(step);
}

function assignmentOwnsMessageSource(
  assignment: AdPlacementAssignment,
  source: MessageEventSource | null,
): boolean {
  if (!source) return false;
  for (const iframe of assignment.container.querySelectorAll("iframe")) {
    if (iframe.contentWindow === source) return true;
  }
  return false;
}

/**
 * Bridges the legacy placeholder model to the SDK: layouts keep rendering
 * their empty `data-layout-position` containers, and this component portals
 * registry-backed `<AdSlot>`s into them (so React context from the
 * surrounding `AdsProvider` still applies). A MutationObserver re-scans when
 * autoload/infinite-scroll appends new placeholders, replacing the legacy
 * gam.js `watch_autoload` flow.
 */
export default function SdkAdPlacements({
  context,
}: {
  context: PageAdsContext;
}) {
  const [assignments, setAssignments] = useState<AdPlacementAssignment[]>([]);
  const assignmentsRef = useRef(assignments);

  useEffect(() => {
    assignmentsRef.current = assignments;
  }, [assignments]);

  useEffect(() => {
    // Fresh scan state per context so a same-route navigation (article →
    // article via in-body links) re-claims placeholders for the new page
    // instead of keeping the first page's context and claimed set.
    const scanState = createScanState();

    // Resolve Grapeshot per story path. Infinite-scroll/autoload roots can
    // append different articles, so reusing page-1 channels for every appended
    // slot would give all new slots the same `gs_channels` value.
    let active = true;
    const gsChannelsByPath = new Map<string, string[]>();
    const readGrapeshotChannels = async (storyPath?: string) => {
      if (!storyPath || gsChannelsByPath.has(storyPath)) return;
      const targeting = await readLegacyGrapeshotTargeting(storyPath).catch(
        () => undefined,
      );
      const channels = targeting?.gs_channels;
      if (active && Array.isArray(channels) && channels.length > 0) {
        gsChannelsByPath.set(storyPath, channels);
      }
    };
    const resolveGsChannels = (container: Element) => {
      const storyPath = readAnalyticsStoryPath(container) ?? context.storyPath;
      return storyPath
        ? { channels: gsChannelsByPath.get(storyPath) }
        : undefined;
    };
    void readGrapeshotChannels(context.storyPath);

    const append = (found: AdPlacementAssignment[]) => {
      setAssignments((prev) =>
        mergeConnectedAssignments(prev, found, (assignment) => {
          releaseAdPlacementClaim(scanState, assignment.container);
        }),
      );
    };

    // Reset assignments with the initial scan for this context in one update
    // (avoids a separate setAssignments([]) that triggers cascading renders).
    const initial = scanAdPlacements(
      context,
      scanState,
      document,
      resolveGsChannels,
    );
    // eslint-disable-next-line react-hooks/set-state-in-effect -- synchronizing React state with external DOM scan on context change
    setAssignments(initial);

    let timer: ReturnType<typeof setTimeout> | undefined;
    let pendingAddedRoots: Element[] = [];
    const observer = new MutationObserver((records) => {
      const addedRoots = collectAddedPlacementRoots(records);
      const hasRemovedPlaceholder = records.some((record) =>
        nodeListHasLayoutPosition(record.removedNodes),
      );
      if (addedRoots.length === 0 && !hasRemovedPlaceholder) return;

      addedRoots.forEach((root) => {
        addUniqueRoot(pendingAddedRoots, root);
      });

      clearTimeout(timer);
      timer = setTimeout(() => {
        const rootsToScan = pendingAddedRoots;
        pendingAddedRoots = [];

        void (async () => {
          await Promise.all(
            rootsToScan
              .filter((root) => root.isConnected)
              .flatMap((root) => collectStoryPaths(root, context.storyPath))
              .map(readGrapeshotChannels),
          );

          if (!active) return;
          // Scope rescans to added roots only. Pure-removal mutations reach
          // here with rootsToScan empty — append([]) prunes disconnected
          // assignments via mergeConnectedAssignments, without a full-document
          // rescan. The initial scan already evaluated every existing
          // placeholder and the claimed WeakSet prevents double-claiming.
          const found = rootsToScan.flatMap((root) =>
            root.isConnected
              ? scanAdPlacements(context, scanState, root, resolveGsChannels)
              : [],
          );
          append(found);
        })();
      }, RESCAN_DEBOUNCE_MS);
    });
    observer.observe(document.body, { childList: true, subtree: true });

    // The design system requests the AI-summary lightbox ad through the
    // legacy SPHMGAM command queue (root.tsx always stubs it); intercept the
    // "aisummary" command instead of loading the legacy engine, while preserving
    // normal Array push semantics for any remaining queue consumers.
    const sdkWindow = window as WindowWithOptionalSphmgam;
    const sphmgam = (sdkWindow.SPHMGAM ??= { cmd: [] });
    const sphmgamCmd = (sphmgam.cmd ??= []);
    // Keep the pre-override property so cleanup restores the exact function
    // (native Array.prototype.push), not a bound wrapper.
    const originalPush = sphmgamCmd.push;
    const handleCommand = (command: unknown) => {
      if (command === "aisummary") {
        append(scanAiSummaryPlacements(context, scanState));
      }
    };
    sphmgamCmd.push = (...commands: unknown[]) => {
      const nextLength = originalPush.apply(sphmgamCmd, commands);
      commands.forEach(handleCommand);
      return nextLength;
    };

    sphmgamCmd.forEach(handleCommand);

    const onMessage = (event: MessageEvent) => {
      const messageData: unknown = event.data;

      // Gallery lightbox paging — the gallery components post
      // `lightbox-open(-image)=N` / `lightbox-change=N` as the user browses;
      // inserted gallery ad placeholder slides serve a placement bucket
      // (legacy handle_lightbox_gallery parity).
      if (typeof messageData === "string") {
        if (event.origin !== window.location.origin) return;
        const slideIndex = LIGHTBOX_MESSAGE_RE.exec(messageData)?.[1];
        if (slideIndex !== undefined) {
          append(
            scanGalleryLightboxPlacements(context, scanState, +slideIndex),
          );
        }
        return;
      }

      if (typeof messageData !== "number") return;

      assignmentsRef.current.forEach((assignment) => {
        if (!assignment.closeOnMessage?.includes(messageData)) return;
        if (!assignmentOwnsMessageSource(assignment, event.source)) return;

        collapseMasContainer(assignment.container, () => {
          setAssignments((prev) => prev.filter((a) => a !== assignment));
        });
      });
    };
    window.addEventListener("message", onMessage);

    return () => {
      active = false;
      clearTimeout(timer);
      observer.disconnect();
      window.removeEventListener("message", onMessage);
      sphmgamCmd.push = originalPush;
    };
  }, [context]);

  return (
    <>
      {assignments.map((assignment) =>
        createPortal(
          <AdSlot
            siteKey={SDK_ADS_SITE_KEY}
            placement={assignment.placement}
            slotName={assignment.slotName}
            page={context.adSection}
            pageType={context.pageType}
            sizes={assignment.sizes}
            sizeMapping={assignment.sizeMapping}
            targeting={{
              ...assignment.targeting,
              ...(assignment.infiniteScrollIndex
                ? { page_number: `${assignment.infiniteScrollIndex}` }
                : undefined),
            }}
            infiniteScrollIndex={assignment.infiniteScrollIndex}
            onSlotRenderEnded={(event) => {
              if (!(assignment.container instanceof HTMLElement)) return;

              // Legacy gam.js parity: an unfilled rail IMU hides its whole
              // sidebar container, otherwise the sticky rail's min-height
              // CSS leaves a tall blank hole.
              if (RAIL_HIDE_ON_EMPTY.has(assignment.placement)) {
                assignment.container.style.display = event.isEmpty
                  ? "none"
                  : "";
              }

              assignment.container.dispatchEvent(
                new CustomEvent(SLOT_RENDER_ENDED_EVENT, {
                  bubbles: true,
                  detail: { isEmpty: event.isEmpty },
                }),
              );
            }}
            // `sphm_ad` keeps the legacy centering/margin CSS hooks; sidebar
            // slots keep the legacy sticky-rail container class on the shell.
            className="sphm_ad"
            wrapperClassName={
              assignment.sticky ? "sphm_ad_sticky_container" : undefined
            }
          />,
          assignment.container,
          assignment.key,
        ),
      )}
    </>
  );
}
